// The website's amounts, from the price book (PRD-1553, ARCH-006 PRD-1553
// amendment point 10). No figure is written by hand: the copy carries a
// placeholder per amount — `{price:<line>@<form>}` — and this module fills it
// from the pricing `/pricing/active.json` answers. The build bakes the same
// pricing into the HTML; the page applies the live one again on load.
//
// Deliberately free of the billing package: this runs in the visitor's
// browser, and the package is a build-time dependency of the site only
// (GEN-004). The annual amount arrives computed, so the site never derives one.

/** What `getPublicPricing` answers: per line, the monthly and the annual net EUR. */
export interface SitePricing {
  readonly version: number;
  readonly lines: Readonly<Record<string, { readonly monthly: number; readonly annual: number }>>;
}

export type SiteLang = 'de' | 'en';

/**
 * `monthly` — the monthly price; `annualMonthly` — the annual price per month,
 * as the page shows it beside the monthly one; `annual` — the annual total.
 */
export type PriceForm = 'monthly' | 'annualMonthly' | 'annual';

/** `{price:base:standard@monthly}` — a line key may itself contain colons. */
export const PRICE_PLACEHOLDER = /\{price:([A-Za-z0-9:-]+)@(monthly|annualMonthly|annual)\}/g;

const FORMATTERS: Record<SiteLang, Intl.NumberFormat[]> = {
  de: [0, 2].map((digits) => new Intl.NumberFormat('de-DE', { minimumFractionDigits: digits, maximumFractionDigits: digits })),
  en: [0, 2].map((digits) => new Intl.NumberFormat('en-GB', { minimumFractionDigits: digits, maximumFractionDigits: digits })),
};

/** `21 €` / `€21`, and cents only where the amount has them: `6,75 €` / `€6.75`. */
export function formatEur(amount: number, lang: SiteLang): string {
  const whole = Number.isInteger(amount);
  const figure = FORMATTERS[lang][whole ? 0 : 1].format(amount);
  return lang === 'de' ? `${figure} €` : `€${figure}`;
}

function amountOf(pricing: SitePricing, line: string, form: PriceForm): number | undefined {
  const entry = pricing.lines[line];
  if (!entry) return undefined;
  if (form === 'monthly') return entry.monthly;
  if (form === 'annual') return entry.annual;
  return Math.round((entry.annual / 12) * 100) / 100;
}

/**
 * Every placeholder of `text` replaced by its formatted amount. A placeholder
 * whose line the pricing lacks stays as it is and is reported, so a line the
 * copy names but the book does not sell is visible rather than priced at 0.
 */
export function fillPrices(text: string, pricing: SitePricing, lang: SiteLang): { text: string; missing: string[] } {
  const missing: string[] = [];
  const filled = text.replace(PRICE_PLACEHOLDER, (placeholder, line: string, form: PriceForm) => {
    const amount = amountOf(pricing, line, form);
    if (amount === undefined) {
      missing.push(line);
      return placeholder;
    }
    return formatEur(amount, lang);
  });
  return { text: filled, missing };
}

/** A translation dictionary with every string's placeholders filled. */
export function pricedTranslations<T>(dict: T, pricing: SitePricing, lang: SiteLang): T {
  const walk = (value: unknown): unknown => {
    if (typeof value === 'string') return fillPrices(value, pricing, lang).text;
    if (Array.isArray(value)) return value.map(walk);
    if (value && typeof value === 'object') {
      return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, walk(entry)]));
    }
    return value;
  };
  return walk(dict) as T;
}

/**
 * Fills the placeholders in the text and the attributes under `root` — the
 * prerender's English document, and the build's check that nothing is left.
 * Returns the lines the pricing lacks.
 */
export function applyPricing(root: Node, pricing: SitePricing, lang: SiteLang): string[] {
  const missing: string[] = [];
  const fill = (text: string): string => {
    const result = fillPrices(text, pricing, lang);
    missing.push(...result.missing);
    return result.text;
  };
  const document = root.ownerDocument ?? (root as Document);
  const walker = document.createTreeWalker(root, 0x1 | 0x4); // elements and text
  for (let node: Node | null = root; node; node = walker.nextNode()) {
    if (node.nodeType === 3 && node.nodeValue?.includes('{price:')) {
      node.nodeValue = fill(node.nodeValue);
    } else if (node.nodeType === 1) {
      for (const attribute of Array.from((node as Element).attributes)) {
        if (attribute.value.includes('{price:')) attribute.value = fill(attribute.value);
      }
    }
  }
  return missing;
}
